import { EmptyTestValueGroup, TestValueGroup } from '../../../interfaces/TestValueGroup';
import { ErrorLevel, useAddError, useClearError } from '../../../context/error';
import { Formik, FormikHelpers } from 'formik';
import { FunctionComponent, useEffect, useState } from 'react';
import { Location, useLocation, useNavigate, useParams } from 'react-router-dom';
import { get, reduce } from 'lodash';

import BareInput from '../../../components/form/BareInput';
import InputGroup from '../../../components/form/InputGroup';
import InputSection from '../../../components/form/InputSection';
import LoadingIndicator from '../../../components/common/LoadingIndicator';
import { Row } from 'react-bootstrap';
import { TestValueGroupService } from '../../../services';
import Title from '../../../components/title';
import { confirm } from '../../../components/dialogs/Confirmation';
import qs from 'query-string';
import validateTestValueGroupName from '../../../helpers/validate/validateTestValueGroupName';

const getChangedValues = (values: TestValueGroup, initialValues: TestValueGroup): Partial<TestValueGroup> => {
  return reduce(
    Object.entries(values),
    (acc, [key, value]) => {
      const hasChanged = (initialValues as any)[key] !== value;
      if (hasChanged) {
        (acc as any)[key] = value;
      }
      return acc;
    },
    {},
  );
};

const shouldEditing = (location: Location) => {
  const queryParams = qs.parse(location.search, {});
  return queryParams.edit === '1' || queryParams.edit === 'true';
};

const Show: FunctionComponent = () => {
  const addError = useAddError();
  const clearError = useClearError();

  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(shouldEditing(location));

  const [item, setItem] = useState<TestValueGroup>(EmptyTestValueGroup);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (!params.id || !params.tgid) {
          return;
        }
        const _item = await TestValueGroupService(addError).get(params.tgid);
        if (!_item || !_item.id) {
          navigate(`/settings/tasks/${params.id}`);
        }
        setItem(_item);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [addError, navigate, params.id, params.tgid]);

  const onSubmit = async (values: TestValueGroup, { setSubmitting }: FormikHelpers<TestValueGroup>) => {
    setSubmitting(true);
    try {
      if (params.tgid) {
        clearError();
        const data = getChangedValues(values, item);
        await TestValueGroupService(addError).update(params.tgid, data);
        setEditing(false);
      }
    } catch (error) {
      addError({
        level: ErrorLevel.danger,
        message: get(error, 'message', 'Unbekannter Fehler'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (setSubmitting: (isSubmitting: boolean) => void) => {
    setSubmitting(true);
    try {
      if (params.tgid) {
        if (
          await confirm(
            'Prüfwertgruppe  löschen?',
            'Der Prüfwertgruppe  wird entgültig gelöscht. Sind Sie sich sicher?',
            'Löschen',
            'Abbruch',
            {
              okColor: 'danger',
            },
          )
        ) {
          await TestValueGroupService(addError).destroy(params.tgid);
          navigate(`/settings/tasks/${params.id}`);
        }
      }
    } catch (error) {
      addError({
        level: ErrorLevel.danger,
        message: get(error, 'message', 'Unbekannter Fehler'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <Formik enableReinitialize={true} initialValues={item} onSubmit={onSubmit}>
      {({ handleSubmit, isValidating, isValid, errors, resetForm, isSubmitting, setSubmitting, values }) => (
        <form autoComplete="off" onSubmit={handleSubmit}>
          <Title
            onEdit={() => setEditing(true)}
            onDelete={item.testValues.length === 0 ? () => onDelete(setSubmitting) : undefined}
            onCancel={() => {
              resetForm({
                values: item,
              });
              setEditing(false);
            }}
            editing={editing}
            editingValid={!isValidating && isValid}
            isSubmitting={isSubmitting}
            to={`/settings/tasks/${params.id}`}
            title={`${item?.testTask?.initials} - ${item.name}`}
          />
          <Row>
            <InputSection title="Stammdaten" xs={12} xl={6}>
              <InputGroup error={errors.name} valid={editing ? !errors.name : undefined} title="Name" name="name">
                <BareInput
                  disabled={!editing || isSubmitting}
                  type="text"
                  name="name"
                  validate={validateTestValueGroupName}
                />
              </InputGroup>
            </InputSection>
          </Row>
        </form>
      )}
    </Formik>
  );
};

export default Show;
